import { useEffect, useRef, useState } from 'react'

import type { FilterOperator } from 'types/filter-page'

type LetterErrorSlot = 'single' | 'start' | 'end'

type UseNumericValidationParams = {
  operator: FilterOperator
  /** Committed filter value — the typed-text mirror resets when it clears. */
  value?: unknown
  onValidityChange?: (_isValid: boolean) => void
}

const NO_ERRORS: Record<LetterErrorSlot, boolean> = {
  single: false,
  start: false,
  end: false,
}

// How long the "only numbers" warning stays visible after a rejected letter.
const LETTER_ERROR_FLASH_MS = 1500

/**
 * Tracks letter-input errors for the number filter inputs: strips non-digits so
 * a letter can never enter the value, and briefly flags the offending input.
 * The flag is a flash (the letter is already gone from the value), so it
 * auto-clears — otherwise it would block submission of a now-valid value.
 */
export const useNumericValidation = ({
  operator,
  value,
  onValidityChange,
}: UseNumericValidationParams) => {
  const [letterErrors, setLetterErrors] =
    useState<Record<LetterErrorSlot, boolean>>(NO_ERRORS)
  // One flash timer per slot, so typing in one input never resets another's.
  const flashTimeouts = useRef<
    Partial<Record<LetterErrorSlot, ReturnType<typeof setTimeout>>>
  >({})

  // Mirrors exactly what's typed (letters stripped only) per slot, so
  // redundant leading zeros ("0006") stay visible while editing — only the
  // digits handed to `apply` (the committed value) have them collapsed. A
  // missing key means "nothing typed yet here" — fall back to the committed
  // value passed in by the caller.
  const [rawText, setRawText] = useState<
    Partial<Record<LetterErrorSlot, string>>
  >({})

  const clearAllTimers = () => {
    Object.values(flashTimeouts.current).forEach(id => clearTimeout(id))
    flashTimeouts.current = {}
  }

  // Reset transient letter errors and typed text whenever the operator changes.
  useEffect(() => {
    clearAllTimers()
    setLetterErrors(NO_ERRORS)
    setRawText({})
  }, [operator])

  // Clear any pending flash timers on unmount.
  useEffect(() => () => clearAllTimers(), [])

  // Clearing the filter (the "Clear" footer button) resets the value without
  // remounting the form, so drop the typed text then. Must bail when already
  // empty: value stays undefined while the popover is open, and setting a
  // fresh object every run would re-render in a loop.
  useEffect(() => {
    if (value === undefined) {
      setRawText(prev => (Object.keys(prev).length ? {} : prev))
    }
  }, [value])

  const hasActiveLetterError =
    operator === 'between'
      ? letterErrors.start || letterErrors.end
      : letterErrors.single

  // A typed letter is stripped from the value, so the badge can't see it.
  // Report it here so submission stays blocked while the warning shows.
  useEffect(() => {
    onValidityChange?.(!hasActiveLetterError)
  }, [hasActiveLetterError, onValidityChange])

  const handleDigitChange = (
    slot: LetterErrorSlot,
    raw: string,
    apply: (_digits: string) => void,
  ) => {
    const lettersStripped = raw.replace(/\D/g, '')
    const hasLetter = lettersStripped !== raw
    setRawText(prev => ({ ...prev, [slot]: lettersStripped }))

    // Collapse meaningless leading zeros ("0006" -> "6", but "0" alone stays
    // "0") only for the committed value — the field itself keeps showing
    // whatever was typed via `rawText`.
    const digits = lettersStripped.replace(/^0+(?=\d)/, '')
    apply(digits)

    clearTimeout(flashTimeouts.current[slot])

    if (hasLetter) {
      // Flash this slot's warning, then auto-clear just this slot so the
      // (already valid) value stays submittable even if the user stops typing.
      setLetterErrors(prev => (prev[slot] ? prev : { ...prev, [slot]: true }))
      flashTimeouts.current[slot] = setTimeout(() => {
        delete flashTimeouts.current[slot]
        setLetterErrors(prev =>
          prev[slot] ? { ...prev, [slot]: false } : prev,
        )
      }, LETTER_ERROR_FLASH_MS)
    } else {
      // A valid digit clears this input's warning immediately.
      delete flashTimeouts.current[slot]
      setLetterErrors(prev => (prev[slot] ? { ...prev, [slot]: false } : prev))
    }
  }

  return {
    letterErrors,
    hasActiveLetterError,
    handleDigitChange,
    rawText,
  }
}
